import { useToast } from '@chakra-ui/react';
import { SelectOption } from '@pelicargo/types';
import { prettyDate, TimeZone } from '@pelicargo/utils';
import { ReactNode, RefObject, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { SimpleCheckbox } from './SimpleCheckbox';
import { SimpleDatePicker } from './SimpleDatePicker';
import { useSimpleFormContext } from './SimpleForm';
import { SimpleInput } from './SimpleInput';
import { SimpleNumber, SimpleNumberProps } from './SimpleNumber';
import { SimplePopoverInput, SimplePopoverInputProps } from './SimplePopoverInput';
import { SimpleReactSelect } from './SimpleReactSelect';
import { SimpleSelect } from './SimpleSelect';
import { SimpleTextarea } from './SimpleTextarea';

export type SimplePopoverInputType =
  | 'input'
  | 'number'
  | 'textarea'
  | 'react-select'
  | 'select'
  | 'date-picker'
  | 'checkbox';

type Props = Omit<SimplePopoverInputProps, 'displayValue'> & {
  name: string;
  label?: string;
  placeholder?: string;
  initialTimeZone?: TimeZone;
  showTime?: boolean;
  formatDisplayValue?: (value: any) => string | ReactNode;
  defaultDisplayValue?: string;
  numberOptions?: Partial<SimpleNumberProps>;
  inputType?: SimplePopoverInputType;

  showClearButton?: boolean;
  options?: any;
  maxOptionsSelected?: number;
  isHighlighted?: boolean;
  highlightColor?: string;
  isMulti?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  isSparkling?: boolean;
  isHorizontal?: boolean;
  isCreatable?: boolean;
  onQueryChange?: (query: string) => void;
};

export const SimpleLabeledPopoverInput = ({
  name,
  label,
  placeholder,
  inputType = 'input',
  showClearButton = false,
  initialTimeZone,
  formatDisplayValue,
  defaultDisplayValue = '-',
  showTime,
  numberOptions,
  options,
  maxOptionsSelected,
  isHighlighted = false,
  isMulti = false,
  isDisabled = false,
  isRequired = false,
  isSparkling = false,
  highlightColor = 'yellow',
  isHorizontal = false,
  isCreatable = false,
  onQueryChange,
  ...rest
}: Props) => {
  const toast = useToast();
  const { validateField, onValid, submitOnBlur } = useSimpleFormContext();
  const { getValues, setValue, control } = useFormContext();
  const value = useWatch({ name, control });

  const displayValue = useMemo(() => {
    let display = formatDisplayValue?.(value);

    const isSelect = inputType === 'select' || inputType === 'react-select';
    if (!display && isSelect) {
      display = options?.find((option: SelectOption) => option.value === value)?.label;
    }

    const isCheckbox = inputType === 'checkbox';
    if (!display && isCheckbox) display = value ? 'Yes' : 'No';

    const isDatePicker = inputType === 'date-picker';
    if (!display && isDatePicker && value) {
      display = prettyDate(value, { timeZone: initialTimeZone, showTime });
    }

    if (!display && Array.isArray(value)) display = value?.join(', ');

    if (!display) display = value;
    if (!display) display = defaultDisplayValue;

    return <>{display}</>;
  }, [formatDisplayValue, value, inputType, defaultDisplayValue, options, initialTimeZone, showTime]);

  const onBlur = async () => {
    if (!submitOnBlur) return;
    /*
    for some reason getValues(name) can still be an empty string even though we change empty strings to null in SimpleSelect
    this ensures that the value is null instead of empty string
    */
    if (getValues(name) === '') {
      setValue(name, null);
    }
    const field = await validateField(name);
    if (!field) return;

    try {
      await onValid?.(field);
      toast({ title: 'Successfully updated', status: 'success' });
    } catch (error: any) {
      toast({
        title: "Oops, that didn't work",
        status: 'error',
        description: error?.message,
      });
    }
  };

  const renderInput = (initialFocusRef: RefObject<HTMLElement>) => {
    switch (inputType) {
      case 'number':
        return (
          <SimpleNumber
            ref={initialFocusRef as RefObject<HTMLInputElement>}
            size="sm"
            name={name}
            placeholder={placeholder}
            ignoreOnBlur
            isDisabled={isDisabled}
            {...numberOptions}
          />
        );
      case 'textarea':
        return (
          <SimpleTextarea
            ref={initialFocusRef as RefObject<HTMLTextAreaElement>}
            size="sm"
            name={name}
            placeholder={placeholder}
            isDisabled={isDisabled}
            ignoreOnBlur
          />
        );
      case 'select':
        return (
          <SimpleSelect
            ref={initialFocusRef as RefObject<HTMLSelectElement>}
            size="sm"
            name={name}
            placeholder={placeholder}
            isDisabled={isDisabled}
            ignoreOnBlur
            options={options}
          />
        );
      case 'react-select':
        return (
          <SimpleReactSelect
            ref={initialFocusRef}
            name={name}
            options={options}
            max={maxOptionsSelected}
            onInputChange={onQueryChange}
            isDisabled={isDisabled}
            isMulti={isMulti}
            type={isCreatable ? 'creatable' : undefined}
          />
        );
      case 'date-picker':
        return (
          // This wrapper takes an untyped `name`, so the date field path stays a plain string.
          <SimpleDatePicker<Record<string, string | Date>>
            // not opening the can of worms that is auto-focus on this
            size="sm"
            name={name}
            initialTimeZone={initialTimeZone}
            isDisabled={isDisabled}
            ignoreOnBlur
            showTime={showTime}
            showClearButton={showClearButton}
          />
        );
      case 'checkbox':
        return (
          <SimpleCheckbox
            ref={initialFocusRef as RefObject<HTMLInputElement>}
            size="sm"
            name={name}
            label={label ?? name}
            isDisabled={isDisabled}
            ignoreOnBlur
          />
        );
      case 'input':
      default:
        return (
          <SimpleInput
            ref={initialFocusRef as RefObject<HTMLInputElement>}
            size="sm"
            name={name}
            placeholder={placeholder}
            isDisabled={isDisabled}
            ignoreOnBlur
          />
        );
    }
  };

  return (
    <SimplePopoverInput
      label={label}
      displayValue={displayValue}
      name=""
      isHighlighted={isHighlighted}
      highlightColor={highlightColor}
      isRequired={isRequired}
      isDisabled={isDisabled}
      {...rest}
      onPopoverBlur={onBlur}
      isSparkling={isSparkling}
      isHorizontal={isHorizontal}
      inputType={inputType}
    >
      {(initialFocusRef) => renderInput(initialFocusRef)}
    </SimplePopoverInput>
  );
};
